/**
 * Historical Trends Tab Component for RNCM Productivity
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line
} from 'recharts';
import { TrendingUp, Activity, Calendar, BarChart3 } from 'lucide-react';
import { TEAMS } from './constants';

export const HistoricalTrendsTab = ({
  disciplineSummaries,
  capacityData
}) => {
  // Calculate avg weekly visits
  const avgWeeklyVisits = disciplineSummaries.length > 0 
    ? Math.round(disciplineSummaries.reduce((s, d) => s + (d.total_visits || 0), 0) / disciplineSummaries.length)
    : 0;

  // Calculate period growth
  const periodGrowth = disciplineSummaries.length >= 2 
    ? ((disciplineSummaries[0]?.total_visits - disciplineSummaries[disciplineSummaries.length - 1]?.total_visits) / (disciplineSummaries[disciplineSummaries.length - 1]?.total_visits || 1) * 100).toFixed(1)
    : null;

  // Calculate team capacity data
  const teamCapacityData = TEAMS.map(team => {
    const teamData = capacityData.filter(c => c.team === team);
    return {
      team,
      totalCapacity: teamData.reduce((s, c) => s + (c.visit_capacity || 0), 0),
      avgCapacity: teamData.length > 0 
        ? Math.round(teamData.reduce((s, c) => s + (c.visit_capacity || 0), 0) / teamData.length) 
        : 0,
      overCapacity: teamData.filter(c => c.planning_outcome === 'Over Capacity').length,
      underCapacity: teamData.filter(c => c.planning_outcome === 'Under Capacity').length
    };
  });

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Productivity Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-500" />
              Productivity Over Time
            </CardTitle>
            <CardDescription>Track productivity trends across periods</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={disciplineSummaries.slice().reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_start" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total_visits" name="Total Visits" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
                  <Line type="monotone" dataKey="rncm_visits" name="RNCM Visits" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                  <Line type="monotone" dataKey="lpn_visits" name="LPN Visits" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Discipline Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-emerald-500" />
              Discipline Trends
            </CardTitle>
            <CardDescription>Visit breakdown by discipline over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={disciplineSummaries.slice().reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_start" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="rncm_visits" name="RNCM" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lpn_visits" name="LPN" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="wound_rn_visits" name="Wound RN" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="prn_visits" name="PRN" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capacity Trends by Team */}
      <Card>
        <CardHeader>
          <CardTitle>Capacity Trends by Team</CardTitle>
          <CardDescription>Historical capacity utilization across teams</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={teamCapacityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="team" />
                <YAxis yAxisId="left" orientation="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="avgCapacity" name="Avg Capacity" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="overCapacity" name="Over Capacity" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="underCapacity" name="Under Capacity" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-indigo-200 dark:border-indigo-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Weekly Visits</p>
                <p className="text-2xl font-bold">{avgWeeklyVisits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Activity className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Period Growth</p>
                <p className="text-2xl font-bold">{periodGrowth !== null ? `${periodGrowth}%` : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Periods Tracked</p>
                <p className="text-2xl font-bold">{disciplineSummaries.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default HistoricalTrendsTab;
